import {Component, HostListener, Input, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {Observable, Subscription} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {EchartsUtils} from "../../../shared/libraries/echarts-utils";
import {AttributionOverviewService} from '../../../shared/services/attribution-overview.service';
import {TranslateService} from "@ngx-translate/core";
import {ColDef} from "@ag-grid-community/core";
import {MatTabGroup} from "@angular/material/tabs";
import {NgxEchartsDirective} from "ngx-echarts";
import {AgGridTemplateFrameworkComponent} from "../../../shared/components/ag-grid/ag-grid-template-framework/ag-grid-template-framework.component";
import {BaseComponent} from "../../../shared/bases/base-component";
import {ReportStore} from "../../../shared/store/report/report.store";
import {AttributionOverviewStore} from '../../../shared/store/quickDashboard/attribution-overview.store';
import {AdRPeriod} from '../../../shared/interfaces/ad-reports';
import {AttributionOverviewReportData} from '../../../shared/store/quickDashboard/attribution-overview';
import {MetricComponentObject} from '../../../shared/classes/metric-component-object';
import {Serializer} from '../../../shared/interfaces/serializer';
import {Channel} from '../../../shared/interfaces/channel';
import {ParamConfig} from '../../../shared/interfaces/params';
import {EChartsOption} from 'echarts';
import {ReportParamsStore} from '../../../shared/store/report/report-params.store';
import {selectBackgroundLoaded} from '../../../shared/store/selectors';
import {AppState} from '../../../shared/store/store';
import {Store} from '@ngrx/store';

@Component({
  selector: 'app-attribution-overview',
  templateUrl: './attribution-overview.component.html',
  styleUrls: ['./attribution-overview.component.scss'],
  providers: [
    AttributionOverviewService,
    ReportStore,
    ReportParamsStore,
    AttributionOverviewStore
  ],
  encapsulation: ViewEncapsulation.None
})
export class AttributionOverviewComponent extends BaseComponent implements OnInit, OnDestroy {
  @ViewChild(MatTabGroup) private readonly matTabGroupC: MatTabGroup;
  @ViewChild('chartMonoMultitouch', { read: NgxEchartsDirective }) private readonly _chartMonoMultitouchD: NgxEchartsDirective;
  @ViewChild('chartMonoMultichannel', { read: NgxEchartsDirective }) private readonly _chartMonoMultichannelD: NgxEchartsDirective;
  @ViewChild('chartTable', { read: NgxEchartsDirective }) private readonly _chartTableD: NgxEchartsDirective;
  @ViewChild('cellName') private readonly _cellName: TemplateRef<any>;
  @ViewChild('cellPercent') private readonly _cellPercent: TemplateRef<any>;

  @Input('active') public active: boolean;

  public readonly channelsTouchpointsTimeCtrl: FormControl<'time' | 'touchpoints' | 'channels'> = this._attributionOverviewStore.channelsTouchpointsTimeCtrl;
  public readonly allPaidCtrl: FormControl<'all' | 'paid'> = this._attributionOverviewStore.allPaidCtrl;

  public tableTimeDefs: Array<ColDef> = [
    {
      field: 'time',
      suppressMenu: true,
      headerValueGetter: _ => this._translateService.instant('attribution_overview.time'),
      sortable: false,
      valueGetter: params => params.data.name,
      cellRendererFramework: AgGridTemplateFrameworkComponent,
      cellRendererParams: _ => {
        return {
          ngTemplate: this._cellName
        };
      }
    },
    {
      field: 'conv',
      suppressMenu: true,
      headerValueGetter: _ => this._translateService.instant('attribution_overview.conversions'),
      sortable: true,
      valueGetter: params => params.data.value,
    },
    {
      field: '%',
      suppressMenu: true,
      sortable: true,
      valueGetter: params => params.data.percent,
      cellRendererFramework: AgGridTemplateFrameworkComponent,
      cellRendererParams: _ => {
        return {
          ngTemplate: this._cellPercent
        };
      }
    }
  ];

  public tableTouchpointsDefs: Array<ColDef> = [
    {
      field: 'touchpoints',
      suppressMenu: true,
      headerValueGetter: _ => this._translateService.instant('attribution_overview.touchpoints'),
      sortable: false,
      valueGetter: params => params.data.name,
      cellRendererFramework: AgGridTemplateFrameworkComponent,
      cellRendererParams: _ => {
        return {
          ngTemplate: this._cellName
        };
      }
    },
    {
      field: 'conv',
      suppressMenu: true,
      headerValueGetter: _ => this._translateService.instant('attribution_overview.conversions'),
      sortable: true,
      valueGetter: params => params.data.value
    },
    {
      field: '%',
      suppressMenu: true,
      sortable: true,
      valueGetter: params => params.data.percent,
      cellRendererFramework: AgGridTemplateFrameworkComponent,
      cellRendererParams: _ => {
        return {
          ngTemplate: this._cellPercent
        };
      }
    }
  ];

  public tableChannelsDefs: Array<ColDef> = [
    {
      field: 'channels',
      suppressMenu: true,
      headerValueGetter: _ => this._translateService.instant('attribution_overview.channels'),
      sortable: false,
      valueGetter: params => params.data.name,
      cellRendererFramework: AgGridTemplateFrameworkComponent,
      cellRendererParams: _ => {
        return {
          ngTemplate: this._cellName
        };
      }
    },
    {
      field: 'conv',
      suppressMenu: true,
      headerValueGetter: _ => this._translateService.instant('attribution_overview.conversions'),
      sortable: true,
      valueGetter: params => params.data.value
    },
    {
      field: '%',
      suppressMenu: true,
      sortable: true,
      valueGetter: params => params.data.percent,
      cellRendererFramework: AgGridTemplateFrameworkComponent,
      cellRendererParams: _ => {
        return {
          ngTemplate: this._cellPercent
        };
      }
    }
  ];

  public readonly form: FormGroup<{
    conversion_metric: FormControl<MetricComponentObject>,
    channel: FormControl<Serializer<Channel>>;
    period: FormControl<AdRPeriod> }> = new FormGroup({
    conversion_metric: new FormControl<MetricComponentObject>(null, [Validators.required]),
    channel: new FormControl<Serializer<Channel>>(null, [Validators.required]),
    period: new FormControl<AdRPeriod>(null, [Validators.required])
  });

  public readonly allLoading$: Observable<boolean> = this._store.select(selectBackgroundLoaded).pipe(map((loaded: boolean): boolean => !loaded));

  public readonly loading$: Observable<boolean> = this._attributionOverviewStore.loading$;
  public readonly report$: Observable<AttributionOverviewReportData> = this._attributionOverviewStore.report$;
  public readonly totalConversions$: Observable<number | null> = this._attributionOverviewStore.totalConversions$;
  public readonly conversionMonotouch$: Observable<number | null> = this._attributionOverviewStore.conversionMonotouch$;
  public readonly conversionMultitouch$: Observable<number | null> = this._attributionOverviewStore.conversionMultitouch$;
  public readonly conversionMonotouchPercentage$: Observable<number | null> = this._attributionOverviewStore.conversionMonotouchPercentage$;
  public readonly conversionMultitouchPercentage$: Observable<number | null> = this._attributionOverviewStore.conversionMultitouchPercentage$;
  public readonly conversionMonochannel$: Observable<number | null> = this._attributionOverviewStore.conversionMonochannel$;
  public readonly conversionMultichannel$: Observable<number | null> = this._attributionOverviewStore.conversionMultichannel$;
  public readonly conversionMonochannelPercentage$: Observable<number | null> = this._attributionOverviewStore.conversionMonochannelPercentage$;
  public readonly conversionMultichannelPercentage$: Observable<number | null> = this._attributionOverviewStore.conversionMultichannelPercentage$;
  public readonly allConversionsAvgDaysToConversion$: Observable<number | null> = this._attributionOverviewStore.allConversionsAvgDaysToConversion$;
  public readonly allConversionsAvgNumbersOfChannels$: Observable<number | null> = this._attributionOverviewStore.allConversionsAvgNumbersOfChannels$;
  public readonly allConversionsAvgNumbersTouchpoints$: Observable<number | null> = this._attributionOverviewStore.allConversionsAvgNumbersTouchpoints$;
  public readonly multitouchpointAvgDaysToConversion$: Observable<number | null> = this._attributionOverviewStore.multitouchpointAvgDaysToConversion$;
  public readonly multitouchpointAvgNumbersOfChannels$: Observable<number | null> = this._attributionOverviewStore.multitouchpointAvgNumbersOfChannels$;
  public readonly multitouchpointAvgNumbersTouchpoints$: Observable<number | null> = this._attributionOverviewStore.multitouchpointAvgNumbersTouchpoints$;
  public readonly multitouchpointPaidAvgDaysToConversion$: Observable<number | null> = this._attributionOverviewStore.multitouchpointPaidAvgDaysToConversion$;
  public readonly multitouchpointPaidAvgNumbersOfChannels$: Observable<number | null> = this._attributionOverviewStore.multitouchpointPaidAvgNumbersOfChannels$;
  public readonly multitouchpointPaidAvgNumbersTouchpoints$: Observable<number | null> = this._attributionOverviewStore.multitouchpointPaidAvgNumbersTouchpoints$;
  public readonly tableTimeData$: Observable<Array<any>> = this._attributionOverviewStore.tableTimeData$;
  public readonly tableTouchpointsData$: Observable<Array<any>> = this._attributionOverviewStore.tableTouchpointsData$;
  public readonly tableChannelsData$: Observable<Array<any>> = this._attributionOverviewStore.tableChannelsData$;

  public readonly pieChartOptionMonoMultitouch$: Observable<EChartsOption> = this._attributionOverviewStore.pieChartOptionMonoMultitouchData$.pipe(
    map((data: Array<any>): EChartsOption => ({
      color: EchartsUtils.colors,
      tooltip: {
        trigger: 'item',
        formatter: (data) => {
          data.name = this._translateService.instant(data.name);
          return EchartsUtils.tooltipFormatterSimple(data);
        }
      },
      series: [
        {
          type: 'pie',
          top: '-20%',
          left: '-20%',
          right: '-20%',
          bottom: '-20%',
          radius: '50%',
          data: data,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          label: {
            formatter: data => this._translateService.instant(data.name)
          }
        }
      ]
    }))
  );

  public readonly pieChartOptionMonoMultichannel$: Observable<EChartsOption> = this._attributionOverviewStore.pieChartOptionMonoMultichannelData$.pipe(
    map((data: Array<any>): EChartsOption => ({
        color: EchartsUtils.colors,
        tooltip: {
          trigger: 'item',
          formatter: (data) => {
            data.name = this._translateService.instant(data.name);
            return EchartsUtils.tooltipFormatterSimple(data);
          }
        },
        series: [
          {
            type: 'pie',
            top: '-20%',
            left: '-20%',
            right: '-20%',
            bottom: '-20%',
            radius: '50%',
            data: data,
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            },
            label: {
              formatter: data => this._translateService.instant(data.name)
            }
          }
        ]
      })
    ));

  public readonly pieChartOptionTable$: Observable<EChartsOption> = this._attributionOverviewStore.tableChartData$.pipe(
    map((data: Array<any>): EChartsOption => ({
      color: EchartsUtils.colors,
      tooltip: {
        trigger: 'item',
        formatter: (data) => {
          data.name = this._translateService.instant(data.name);
          return EchartsUtils.tooltipFormatterSimple(data);
        }
      },
      series: [
        {
          type: 'pie',
          radius: '50%',
          data: data,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          label: {
            formatter: data => this._translateService.instant(data.name)
          }
        }
      ]
    }))
  );

  public readonly paramsConfigs$: Observable<Array<ParamConfig>> = this._reportStore.paramsConfigs$;
  public readonly paramsDropdownConfigs$: Observable<Array<ParamConfig>> = this._reportStore.paramsDropdownConfigs$;
  public readonly actionsConfigs$: Observable<Array<ParamConfig>> = this._reportStore.actionsConfigs$;
  public readonly loaded$: Observable<boolean> = this._attributionOverviewStore.loaded$;

  private subscription: Subscription;

  constructor(
    private readonly _translateService: TranslateService,
    private readonly _reportStore: ReportStore,
    private readonly _attributionOverviewStore: AttributionOverviewStore,
    private readonly _store: Store<AppState>
  ) {
    super();
  }

  ngOnInit(): void {
    this._reportStore.initLocalStorage(
      'attribution-overview-params',
      ['attributed_metric', 'channel_not_required', 'new_period_without_comparison'],
      ['local_save_attribution_overview'],
      []
    );

    this.subscription = this.channelsTouchpointsTimeCtrl.valueChanges.pipe(
      tap((value: 'time' | 'touchpoints' | 'channels'): void => {
        switch (value) {
          case 'time':
            this.matTabGroupC.selectedIndex = 0;
            break;
          case 'touchpoints':
            this.matTabGroupC.selectedIndex = 1;
            break;
          case 'channels':
            this.matTabGroupC.selectedIndex = 2;
            break;
        }

      })
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  @HostListener('window:resize', ['$event']) private _onResize(): void {
    this._chartMonoMultichannelD?.resize();
    this._chartMonoMultitouchD?.resize();
    this._chartTableD?.resize();
  }

}
